import React, { useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import RetrievalPreview from './RetrievalPreview';
import KnowledgeBaseService from '../../../Services/KnowledgeBaseService';

/**
 * WHAT THE KNOWLEDGE BASE CAN ACTUALLY ANSWER, beside the box you write in.
 *
 * ── WHY IT IS HERE ──
 * Guidance that says "quote only from the knowledge base" is a rule about
 * content the author cannot see from this screen. Written blind, it either
 * over-promises (the KB does not hold that route) or under-uses what is
 * indexed. The email KB is live with ~49,500 documents, so "can it answer
 * this?" is a question with a real answer — and the honest place to ask it is
 * while writing the sentence that depends on it.
 *
 * ── IT IS GLUE, NOT A NEW SCREEN ──
 * `RetrievalPreview` already does the whole job — query, limit, scope,
 * per-chunk scores — and is the same component the Knowledge Bases area
 * renders on its Retrieval tab. Nothing about retrieval is reimplemented
 * here; this adds a base picker and a disclosure, because the builder's
 * author needs the same tool from a different desk. No new endpoint.
 *
 * ── WHAT IT DOES NOT CLAIM ──
 * This is NOT "what the agent sees". Which base `query_knowledge_base`
 * reaches is decided server-side, and a picker here would be inventing a
 * mapping the dashboard does not own. So it is framed as an authoring aid:
 * search the content, then write the rule. Saying more than that would be a
 * guess dressed as a fact.
 *
 * ── CLOSED, AND LOADS NOTHING, UNTIL ASKED ──
 * The builder is already a long screen and most edits are one sentence in the
 * prose. Opening it is what fetches the list.
 */
function GuidanceKnowledgeBase() {
  const [open, setOpen] = useState(false);
  const [bases, setBases] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const openAndLoad = async () => {
    setOpen(true);
    if (bases) return;
    setLoading(true);
    setError('');
    try {
      const list = await KnowledgeBaseService.list();
      const safe = Array.isArray(list) ? list : [];
      setBases(safe);
      // One base is not a choice, so it is simply the one being searched.
      if (safe.length === 1) setSelectedId(safe[0].id);
    } catch (err) {
      setBases([]);
      setError(err.response?.data?.message || err.message || 'Failed to load the knowledge bases');
    } finally {
      setLoading(false);
    }
  };

  const selected = (bases || []).find((base) => base.id === selectedId) || null;

  if (!open) {
    return (
      <Button
        variant="link"
        className="ai-kb-open"
        onClick={openAndLoad}
      >
        <i className="fa fa-book me-2" aria-hidden="true" />
        Search the knowledge base while you write
      </Button>
    );
  }

  return (
    <section className="ai-panel ai-kb">
      <div className="ai-panel-title d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>
          <i className="fa fa-book me-2" aria-hidden="true" />
          Search the knowledge base
        </span>
        <Button size="sm" variant="outline-secondary" onClick={() => setOpen(false)}>
          Close
        </Button>
      </div>

      <p className="ai-panel-note">
        {'What is actually indexed, so a rule like "quote only from the'
         + ' knowledge base" is written against real content. This searches the'
         + ' base you pick — it is not a statement about which base the agent'
         + ' itself reaches, which the server decides.'}
      </p>

      {error && <p className="ai-decision-error">{error}</p>}

      {loading && (
        <p className="ai-panel-note mb-0">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading the knowledge bases…
        </p>
      )}

      {!loading && bases && bases.length === 0 && (
        <p className="mb-0">There are no knowledge bases to search.</p>
      )}

      {!loading && bases && bases.length > 1 && (
        <Form.Group className="mb-3" controlId="ai-kb-pick">
          <Form.Label className="ai-panel-label">Knowledge base</Form.Label>
          <Form.Select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">Pick a base to search…</option>
            {bases.map((base) => (
              <option key={base.id} value={base.id}>{base.name || base.id}</option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      {/* The Knowledge Bases area's own Retrieval tab, unchanged. */}
      {selected && <RetrievalPreview knowledgeBase={selected} />}
    </section>
  );
}

export default GuidanceKnowledgeBase;
